import { useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { CreditCard, Instagram } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const { t } = useLanguage();
  const [nlEmail, setNlEmail] = useState("");

  const handleSubscribe = async () => {
    if (!nlEmail.trim() || !nlEmail.includes("@")) { toast.error("Enter a valid email"); return; }
    try {
      const res = await axios.post(`${API}/newsletter`, { email: nlEmail.trim() });
      toast.success(res.data.message);
      setNlEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Subscription failed");
    }
  };

  const linkClass = "text-xs tracking-wide text-[#FDFBF7]/70 hover:text-[#FDFBF7] transition-colors";

  return (
    <footer data-testid="main-footer" className="bg-[#1A1A1A] text-[#FDFBF7]">
      <div className="px-6 md:px-12 lg:px-24 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-16">
          {/* Brand */}
          <div>
            <img
              src="https://customer-assets.emergentagent.com/job_luxury-handbags-7/artifacts/unol57i8_IMG_3217.jpeg"
              alt="Mona Luxe"
              className="h-12 object-contain mb-6"
            />
            <p className="text-xs leading-relaxed text-[#9CA3AF] tracking-wide">
              {t("footer.about_text")}
            </p>
          </div>

          {/* Customer Care */}
          <div>
            <h4 className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF] mb-6">
              {t("footer.customer_care")}
            </h4>
            <ul className="space-y-3">
              <li><Link to="/shipping" className={linkClass} data-testid="footer-shipping-link">{t("footer.shipping_info")}</Link></li>
              <li><Link to="/returns" className={linkClass} data-testid="footer-returns-link">{t("footer.returns_policy")}</Link></li>
              <li><Link to="/contact" className={linkClass} data-testid="footer-contact-link">{t("footer.contact")}</Link></li>
              <li><Link to="/faq" className={linkClass} data-testid="footer-faq-link">{t("footer.faq")}</Link></li>
              <li><Link to="/authenticity" className={linkClass} data-testid="footer-authenticity-link">{t("footer.authenticity")}</Link></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF] mb-6">
              {t("footer.legal")}
            </h4>
            <ul className="space-y-3">
              <li><Link to="/privacy" className={linkClass} data-testid="footer-privacy-link">{t("footer.privacy")}</Link></li>
              <li><Link to="/terms" className={linkClass} data-testid="footer-terms-link">{t("footer.terms")}</Link></li>
              <li><Link to="/cookies" className={linkClass} data-testid="footer-cookies-link">{t("footer.cookies")}</Link></li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF] mb-6">
              {t("footer.newsletter")}
            </h4>
            <p className="text-xs text-[#FDFBF7]/70 mb-4 leading-relaxed">
              {t("footer.newsletter_text")}
            </p>
            <div className="flex">
              <input
                type="email"
                placeholder={t("footer.email_placeholder")}
                value={nlEmail}
                onChange={(e) => setNlEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSubscribe()}
                className="flex-1 bg-transparent border-b border-[#FDFBF7]/30 text-xs py-2 px-0 outline-none text-[#FDFBF7] placeholder:text-[#FDFBF7]/30 tracking-wide"
                data-testid="newsletter-email"
              />
              <button
                onClick={handleSubscribe}
                className="text-[10px] uppercase tracking-[0.15em] ml-4 text-[#FDFBF7]/70 hover:text-[#FDFBF7] transition-colors"
                data-testid="newsletter-subscribe"
              >
                {t("footer.subscribe")}
              </button>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-16 pt-8 border-t border-[#FDFBF7]/10 flex flex-col items-center gap-5">
          <a
            href="https://www.instagram.com/monaluxe_nl?igsh=aTluOXU4ZXVwYnRj&utm_source=qr"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#FDFBF7]/40 hover:text-[#FDFBF7] transition-colors"
            data-testid="footer-instagram-link"
          >
            <Instagram size={20} strokeWidth={1.5} />
          </a>
          <div className="flex items-center gap-5">
            <div className="flex items-center gap-1 text-[#FDFBF7]/40">
              <CreditCard size={16} strokeWidth={1.5} />
              <span className="text-[10px] tracking-wider">VISA</span>
            </div>
            <div className="flex items-center gap-1 text-[#FDFBF7]/40">
              <CreditCard size={16} strokeWidth={1.5} />
              <span className="text-[10px] tracking-wider">MASTERCARD</span>
            </div>
            <span className="text-[10px] tracking-wider text-[#FDFBF7]/40">iDEAL</span>
            <span className="text-[10px] tracking-wider text-[#FDFBF7]/40">APPLE PAY</span>
          </div>
          <p className="text-[10px] text-[#FDFBF7]/40 tracking-wider uppercase">
            &copy; {new Date().getFullYear()} Mona Luxe. {t("footer.rights")}
          </p>
        </div>
      </div>
    </footer>
  );
}
